import { api } from "./api";

export type PlannedRoute = {
    id: number;
    customerId: number;
    departmentId: number | null;
    name: string;
    description: string | null;
    encodedPolyline: string;
    source: "GOOGLE_DIRECTIONS";
    plannedDistanceMeters: number | null;
    plannedDurationSeconds: number | null;
    copyrights: string | null;
    version: number;
    activated: boolean;
};

export type PlannedRouteRequest = Omit<PlannedRoute, "id" | "version" | "activated">;

export type PlannedRouteSummary = Pick<PlannedRoute, "id" | "customerId" | "departmentId" | "name" | "plannedDistanceMeters" | "version" | "activated">;

export type RouteSchedule = {
    id: number;
    routeId: number;
    routeName: string;
    vehicleId: number;
    weekdays: number[];
    startTime: string;
    endTime: string;
    effectiveStart: string;
    effectiveEnd: string | null;
    timeZone: string;
    corridorMeters: number;
    pricePerLiter: number | null;
    activated: boolean;
};

export type RouteScheduleRequest = Omit<RouteSchedule, "id" | "routeName" | "timeZone" | "activated"> & { customerId: number };

export type VehicleRouteReportRow = {
    scheduleId: number;
    routeId: number;
    routeName: string;
    serviceDate: string;
    startUtc: string;
    endUtcExclusive: string;
    timeZone: string;
    corridorMeters: number;
    status: "NOT_EVALUATED" | "PENDING_EVALUATION" | "PROCESSING" | "NO_DATA" | "DATA_LIMIT" | "INSUFFICIENT_DATA" | "COMPLIANT" | "DEVIATED";
    validPoints: number | null;
    outsidePoints: number | null;
    longestOutsideSequence: number | null;
};

export type RouteTrackPoint = {
    fixTimeUtc: string;
    latitude: number;
    longitude: number;
    outsideCorridor: boolean;
    driverIdentifier: string | null;
    driverId: number | null;
    driverName: string | null;
    positionId?: number | null;
    speed?: number | null;
    address?: string | null;
    fuelLiters?: number | null;
    fuelPercentage?: number | null;
    fuelLitersEstimated?: boolean;
    fuelPercentageEstimated?: boolean;
};
export type RouteMetrics = {
    plannedDistanceKm: number | null;
    actualDistanceKm: number | null;
    distanceSource: string | null;
    spentFuelLiters: number | null;
    fuelConsumptionEstimated: boolean;
    currentFuelLiters: number | null;
    currentFuelPercentage: number | null;
    currentFuelLitersEstimated: boolean;
    currentFuelPercentageEstimated: boolean;
    lastPositionTimeUtc: string | null;
    pricePerLiter: number | null;
    estimatedFuelCost: number | null;
    partial: boolean;
};
export type RouteDriverSummary = { driverId: number; driverName: string; positionCount: number };
export type VehicleRouteMap = {
    row: VehicleRouteReportRow;
    encodedPlannedPolyline: string;
    actualPoints: RouteTrackPoint[];
    displayedPointsSampled: boolean;
    drivers: RouteDriverSummary[];
    unidentifiedPoints: number;
    metrics?: RouteMetrics | null;
};
export type RouteDeviationAlert = {
    evaluationId: number;
    vehicleId: number;
    vehicleName: string;
    vehiclePlate: string;
    routeId: number;
    routeName: string;
    serviceDate: string;
    timeZone: string;
    outsidePoints: number;
    longestOutsideSequence: number;
    createdAtUtc: string;
};

const auth = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export const routeApi = {
    list: async (token: string, customerId: number): Promise<PlannedRouteSummary[]> =>
        (await api.get(`/routes/customer/${customerId}`, auth(token))).data,
    find: async (token: string, customerId: number, id: number): Promise<PlannedRoute> =>
        (await api.get(`/routes/customer/${customerId}/${id}`, auth(token))).data,
    create: async (token: string, request: PlannedRouteRequest): Promise<PlannedRoute> =>
        (await api.post("/routes", request, auth(token))).data,
    update: async (token: string, id: number, request: PlannedRouteRequest): Promise<PlannedRoute> =>
        (await api.put(`/routes/${id}/customer/${request.customerId}`, request, auth(token))).data,
    setActive: async (token: string, customerId: number, id: number, active: boolean): Promise<PlannedRoute> =>
        (await api.put(`/routes/${id}/customer/${customerId}/active`, null, { ...auth(token), params: { active } })).data,
    schedules: async (token: string, customerId: number, vehicleId: number): Promise<RouteSchedule[]> =>
        (await api.get(`/routes/customer/${customerId}/vehicle/${vehicleId}/schedules`, auth(token))).data,
    createSchedule: async (token: string, request: RouteScheduleRequest): Promise<RouteSchedule> =>
        (await api.post("/routes/schedules", request, auth(token))).data,
    setScheduleActive: async (token: string, customerId: number, id: number, active: boolean): Promise<RouteSchedule> =>
        (await api.put(`/routes/schedules/${id}/customer/${customerId}/active`, null, { ...auth(token), params: { active } })).data,
    vehicleReport: async (token: string, customerId: number, vehicleId: number, startDate: string, endDate: string): Promise<VehicleRouteReportRow[]> =>
        (await api.get(`/routes/customer/${customerId}/vehicle/${vehicleId}/report`, { ...auth(token), params: { startDate, endDate } })).data,
    routeMap: async (token: string, customerId: number, vehicleId: number, scheduleId: number, serviceDate: string): Promise<VehicleRouteMap> =>
        (await api.get(`/routes/customer/${customerId}/vehicle/${vehicleId}/map`, { ...auth(token), params: { scheduleId, serviceDate } })).data,
    alerts: async (token: string, customerId: number, sinceDate: string, departmentId?: number): Promise<RouteDeviationAlert[]> =>
        (await api.get(`/routes/customer/${customerId}/alerts`, {
            ...auth(token),
            params: departmentId && departmentId > -1 ? { sinceDate, departmentId } : { sinceDate },
        })).data,
};
